import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import BackupApi from '../../api/BackupApi';
import { BACKUP_SUPPORTED_TYPES } from '../../component/Constants';
import FilterPanel from '../../component/FilterPanel';
import SearchInput from '../../component/SearchInput';
import RefreshButton from '../../component/buttons/RefreshButton';
import Breadcrumb from '../../container/breadcrumb';
import PageContent from '../../container/page_content';
import ConditionalRender from '../../helpers/ConditionalRender';
import { useInstanceFilters } from '../../hooks/useInstanceFilters';
import BackupSummary from '../../types/backup/BackupSummary';
import InstanceTarget from '../../types/instance/InstanceTarget';
import BackupChainTable from './BackupChainTable';
import BackupStatCards from './BackupStatCards';
import BackupHistorySlideout from './BackupHistorySlideout';
import {
    BACKUP_CARD_FILTER_LABELS,
    BACKUP_PERIODS,
    BackupCardFilter,
    backupStats,
    ChainRow,
    DEFAULT_BACKUP_PERIOD_DAYS,
    groupByDatabase,
    isSystemDatabase,
    matchesCardFilter,
} from './backupUtils';

import './style.scss';

interface Props {
    instances: InstanceTarget[];
    toggleMenu: Function;
}

const EXCLUDE_SYSTEM_DATABASES = 'Exclude';

// The current backup chain of every monitored database. Always as of now,
// so there is no time picker; the period only sets the worst RPO window.
function Backups(props: Props) {
    const location = useLocation();
    const supported = useMemo(() => props.instances.filter(
        instance => BACKUP_SUPPORTED_TYPES.includes(instance.type)), [props.instances]);
    const instanceFilters = useInstanceFilters({ instances: supported, shouldFetch: false, skipHostFetch: true });
    const [selectedInstances, setSelectedInstances] = useState<string[]>([]);
    const [systemDatabases, setSystemDatabases] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [cardFilter, setCardFilter] = useState<BackupCardFilter>(null);
    const [days, setDays] = useState(DEFAULT_BACKUP_PERIOD_DAYS);
    const [rows, setRows] = useState<BackupSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<ChainRow | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const requestId = useRef(0);
    const handledDeepLink = useRef('');

    const fetchData = useCallback(async () => {
        // Keep only the latest response if the period changes mid-request.
        const id = ++requestId.current;
        setLoading(true);
        const data = await BackupApi.getSummary(days);
        if (id === requestId.current) {
            setRows(data);
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Event history links pass the database and instance that raised a backup
    // alert so Backups can open the relevant history directly.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const database = params.get('database');
        const instanceIdParam = params.get('id');
        const instanceId = Number(instanceIdParam);
        if (!database || !instanceIdParam || !Number.isFinite(instanceId) || loading ||
            handledDeepLink.current === location.search) {
            return;
        }
        const candidates = groupByDatabase(rows).filter(candidate =>
            candidate.replicas.some(replica => replica.id === instanceId));
        const row = candidates.find(candidate => candidate.database === database) ??
            candidates.find(candidate => candidate.database.toLowerCase() === database.toLowerCase());
        if (row) {
            handledDeepLink.current = location.search;
            setSearch(database);
            setSelected(row);
        }
    }, [location.search, rows, loading]);

    const refresh = useCallback(() => {
        setRefreshKey(key => key + 1);
        fetchData();
    }, [fetchData]);

    const openHistory = (row: ChainRow) => {
        setSelected(row);
        refresh();
    };

    const visibleIds = useMemo(() => new Set(instanceFilters.getFilteredInstances(supported)
        .filter(instance => selectedInstances.length === 0 || selectedInstances.includes(instance.name))
        .map(instance => instance.id)), [instanceFilters, supported, selectedInstances]);

    const filteredRows = useMemo(() => {
        const text = search.trim().toLowerCase();
        const excludeSystem = systemDatabases.includes(EXCLUDE_SYSTEM_DATABASES);
        return rows.filter(row => visibleIds.has(row.id) &&
            !(excludeSystem && isSystemDatabase(row)) &&
            (!text || row.database.toLowerCase().includes(text)));
    }, [rows, visibleIds, systemDatabases, search]);

    // One row per database, after the instance filter so that the replicas
    // shown are the filtered ones. The cards count these rows; a clicked card
    // only narrows the chain.
    const databases = useMemo(() => groupByDatabase(filteredRows), [filteredRows]);
    // Keep the open database's chain status current even if a card filter
    // stops showing its row after the refresh.
    const selectedRow = selected
        ? databases.find(row => row.databasekey === selected.databasekey) ?? selected
        : null;
    const stats = useMemo(() => backupStats(databases), [databases]);
    const chainRows = useMemo(() => databases.filter(row => matchesCardFilter(row, cardFilter)),
        [databases, cardFilter]);

    // Nothing left to show, e.g. after a refresh or another filter.
    useEffect(() => {
        if (!loading && ((cardFilter === 'backedup' && stats.backedUp === 0) ||
            (cardFilter === 'never' && stats.notBackedUp === 0) ||
            (cardFilter === 'attention' && stats.needsAttention === 0))) {
            setCardFilter(null);
        }
    }, [loading, cardFilter, stats]);

    // Instances collecting backups whose login lacks a permission.
    const missingPermissions = useMemo(() => supported.filter(instance =>
        instance.collectbackuphistory !== false &&
        (instance.hasbackuphistory === false || instance.hasbackupfailures === false)), [supported]);

    const filterGroups = [
        {
            name: 'type', label: 'DB Type', options: instanceFilters.availableFilterOptions.types,
            selectedValues: instanceFilters.filters.type,
            onSelectionChange: (values: string[]) => instanceFilters.updateFilter('type', values),
        },
        {
            name: 'instance', label: 'Instance', options: supported.map(instance => instance.name).sort(),
            selectedValues: selectedInstances, onSelectionChange: setSelectedInstances,
        },
        {
            name: 'tag', label: 'Tag', options: instanceFilters.availableFilterOptions.tags,
            selectedValues: instanceFilters.filters.tag,
            onSelectionChange: (values: string[]) => instanceFilters.updateFilter('tag', values),
        },
        {
            name: 'systemdatabases', label: 'System Databases', options: [EXCLUDE_SYSTEM_DATABASES],
            selectedValues: systemDatabases, onSelectionChange: setSystemDatabases,
        },
    ];

    const clearAllFilters = () => {
        instanceFilters.clearAllFilters();
        setSelectedInstances([]);
        setSystemDatabases([]);
    };

    const period = BACKUP_PERIODS.find(p => p.days === days)?.label ?? '';
    const closeSlideout = useCallback(() => setSelected(null), []);

    return <PageContent title="Backups - DBmarlin" description="Backup history of the monitored databases">
        <div className="backups">
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Backups">
                    <Link to="/backups">Analysis</Link>
                    <NavLink to="/backups" className="current-breadcrumb-link">Backups</NavLink>
                </Breadcrumb>
                <div className="col col-lg-5 col-md-6 col-sm-6 order-last icon text-md-end actions ms-auto">
                    <RefreshButton refresh={refresh} visible/>
                    <button type="button" className="btn btn-heading btn-menu d-1200-none"
                            onClick={() => props.toggleMenu()}>
                        <i className="fal fa-bars fa-fw"/>
                    </button>
                </div>
            </div>

            <div className="loader">
                <ConditionalRender if={loading}>
                    <div className="bar"/>
                </ConditionalRender>
            </div>

            <ConditionalRender if={missingPermissions.length > 0}>
                <div className="alert alert-warning d-flex align-items-center py-2 px-3 mt-2" role="alert">
                    <i className="fas fa-triangle-exclamation me-2" aria-hidden="true"/>
                    <span>
                        Backup monitoring is incomplete for {missingPermissions.map(i => i.name).join(', ')}.
                        Backup history needs SELECT on msdb.dbo.backupset, backupmediaset and
                        backupmediafamily, and failed backups need VIEW SERVER STATE to read the error log.
                    </span>
                </div>
            </ConditionalRender>

            <div className="row mt-2">
                <div className="col">
                    <FilterPanel filterGroups={filterGroups} onClearAll={clearAllFilters} isLoading={loading}/>
                </div>
            </div>

            <div className="mt-2">
                <BackupStatCards stats={stats} loading={loading} filter={cardFilter} onFilterChange={setCardFilter}/>
            </div>

            <div className="row mt-2">
                <div className="col">
                    <div className="card">
                        <div className="card-header">
                            <i className="fal fa-box-archive fa-fw" aria-hidden="true"/> Current backup chain
                            <div className="btn-group float-end">
                                <button id="btnBackupsPeriod" type="button"
                                        className="btn btn-xsm btn-primary dropdown-toggle"
                                        data-bs-toggle="dropdown" aria-expanded="false">
                                    Worst RPO: {period.toLowerCase()}
                                </button>
                                <ul className="dropdown-menu border" aria-labelledby="btnBackupsPeriod">
                                    {BACKUP_PERIODS.map(option => <li key={option.days}>
                                        <span className={`dropdown-item ${option.days === days ? 'active' : ''}`}
                                              role="button" onClick={() => setDays(option.days)}>
                                            {option.label}
                                        </span>
                                    </li>)}
                                </ul>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row table-search mb-2">
                                <div className="col text-muted small text-start">
                                    The latest full, differential and log backups of each database. Only a
                                    differential taken since the latest full is shown.<br/>
                                    Worst RPO is the longest time without a new backup to restore from.
                                </div>
                                {cardFilter && <div className="col-auto">
                                    <span className="badge rounded-pill bg-primary backups__card-filter">
                                        Showing: {BACKUP_CARD_FILTER_LABELS[cardFilter]}
                                        <button type="button" className="btn-close btn-close-white ms-2"
                                                aria-label="Show all databases" onClick={() => setCardFilter(null)}/>
                                    </span>
                                </div>}
                                <div className="col col-md-3">
                                    <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
                                                 onClear={() => setSearch('')} placeholder="Search databases..."/>
                                </div>
                            </div>
                            <BackupChainTable rows={chainRows} loading={loading}
                                              selected={selectedRow} onSelect={openHistory}/>
                        </div>
                    </div>
                </div>
            </div>

            <BackupHistorySlideout row={selectedRow} days={days} refreshKey={refreshKey}
                                   onRefresh={refresh} onClose={closeSlideout}/>
        </div>
    </PageContent>;
}

export default Backups;
